/** @format */

import { Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button/button';
import { CopyIcon } from './icons';

type ShareModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  shareUrl?: string;
};

export const ShareModal = ({ isOpen, onClose, shareUrl = '' }: ShareModalProps) => {
  const { t } = useTranslation();
  const [tooltipText, setTooltipText] = useState(() => t('share.copyLink'));
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setTooltipText(t('share.copied'));

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setTooltipText(t('share.copyLink'));
      }, 1000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <Dialog
      open={Boolean(isOpen)}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="share-modal-title"
      PaperProps={{ className: 'share-modal' }}>
      <DialogTitle id="share-modal-title" className="share-modal__title share-modal__heading" component="h2">
        {t('share.title')}
      </DialogTitle>
      <DialogContent className="share-modal__dialog-content">
        <div className="share-modal__input-wrapper">
          <input type="text" className="share-modal__input" value={shareUrl} readOnly />
          <div className="share-modal__copy-btn-wrapper">
            <button
              type="button"
              className="share-modal__copy-btn"
              onClick={handleCopy}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              aria-label={t('share.copyLinkAria')}>
              <CopyIcon width={24} height={24} color="#1F1F1F" />
            </button>
            {showTooltip ? <div className="share-modal__tooltip">{tooltipText}</div> : null}
          </div>
        </div>
      </DialogContent>
      <DialogActions className="share-modal__actions">
        <Button label={t('common.button.close')} onClick={onClose} />
      </DialogActions>
    </Dialog>
  );
};
